import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import { CDPrice as Price } from '~/components/Price/CDPrice';
import type { ProductViewProps } from '~/pages/Product/Product';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { getApplicableMultibuyLevel } from '~/utils/multibuy';

import styles from './ProductPrice.module.scss';

const ProductPrice = ({
  isMultiBuy,
  currentPrice,
  currentItem,
  isLoading,
  count,
}: Pick<
  ProductViewProps,
  'isMultiBuy' | 'currentItem' | 'currentPrice' | 'isLoading' | 'count'
>) => {
  const applicableLevel = currentItem
    ? getApplicableMultibuyLevel(currentItem.multibuyPriceLevels, count)
    : null;
  const multibuyActivated = applicableLevel !== null;
  const isMarketplace =
    company.isMarketplaceVariant || company.useMarketplaceProductLayout;

  const discount = useMemo(() => {
    if (isMultiBuy && multibuyActivated) {
      return `${currentPrice}`;
    }

    if (currentItem?.discountPrice) {
      return currentItem?.priceFormatted;
    }
  }, [isMultiBuy, multibuyActivated, currentPrice, currentItem]);
  const hasDiscount = Boolean(discount);
  const formattedCurrentPrice = formatPriceWithCurrency(currentPrice ?? 0);
  const formattedOldPrice = hasDiscount
    ? formatPriceWithCurrency(discount ?? 0)
    : null;

  if (isLoading) {
    return (
      <div className={styles.price}>
        {!company.is('mcosmetic') && (
          <div className={styles.oldPrice}>
            <Skeleton containerClassName={styles.skeleton} />
          </div>
        )}
        <div className={styles.currentPrice}>
          <Skeleton containerClassName={styles.skeleton} />
        </div>
      </div>
    );
  }
  if (isMarketplace) {
    return (
      <div className={styles.priceMarketplace}>
        <div className={styles.priceMarketplaceRow}>
          <span
            className={classes([
              styles.currentPriceMarketplace,
              hasDiscount && styles.currentPriceMarketplaceDiscounted,
            ])}
          >
            {formattedCurrentPrice}
          </span>
          {hasDiscount && formattedOldPrice && (
            <span className={styles.oldPriceMarketplace}>
              {formattedOldPrice}
            </span>
          )}
          {hasDiscount && currentItem?.discountPercentage && (
            <span className={styles.discountPercentageMarketplace}>
              {currentItem.discountPercentage}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <Price
      price={currentPrice ?? 0}
      discount={isMultiBuy ? undefined : discount}
      percentDelimiter={isMultiBuy && !multibuyActivated}
      discountPercent={currentItem?.discountPercentage}
      classes={{
        root: styles.price,
        classNameOldPrice: styles.oldPrice,
        classNameCurrentPrice: classes([
          styles.currentPrice,
          isMarketplace &&
            hasDiscount &&
            styles.currentPriceMarketplaceDiscounted,
        ]),
        classNameDiscountPercent: classes([
          styles.discountPercentage,
          isMarketplace && styles.discountPercentageMarketplace,
        ]),
      }}
    />
  );
};

export default observer(ProductPrice);
